import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@components/ui/button";
import {
  ArrowLeft,
  ArrowRight,
  CirclePlus,
  Trash,
  SquarePen,
  Eye,
  Loader,
  FileText,
  SlidersHorizontal,
  Settings,
  Zap,
  ChevronDown,
  Save,
  Settings2,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@components/ui/tabs";
import { toast } from "@components/ui/use-toast";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  fetchUIPageData,
  saveUIPageInfo,
  fetchConfigPage,
} from "../plugin/createPluginApi";
import { getAllWorkflows } from "./createPluginApis";
import { UIPageType } from "../plugin/allData";
import SettingsUIPage from "../plugin/SettingsUIPage";
import ActionsPage from "../plugin/ActionsPage";
import HelpDocumentDialog from "../plugin/HelpDocumentDialog";
import UiPreview from "../plugin/UiPreview";
import Confirmation from "@components/common/ConfirmDialog";
import PageLoader from "@components/loaders/PageLoader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import { Textarea } from "@components/ui/textarea";
import {
  MeshPluginProvider,
  useMeshPluginContext,
  MeshPluginActions,
} from "@services/providers/MeshPlugin";
import FallBackUi from "../plugin/FallBackUi";
import { useUnsavedChanges, UnsavedChangesDialog } from "./Unsavedchangesguard";

const CreatePluginUIPageContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = location.state?.isEdit || false;
  const queryClient = useQueryClient();

  const {
    pluginId: contextPluginId,
    uiPages,
    helpDocs,
    fields,
    dispatch,
    hasTempConfigPage,
  } = useMeshPluginContext();

  const { hasUIPage } = useMeshPluginContext();

  const [pluginId, setPluginId] = React.useState<any>(null);
  const [activeTab, setActiveTab] = React.useState("settings");
  const [openPreview, setOpenPreview] = React.useState(false);
  const [openAddHelpDocumentDialog, setOpenAddHelpDocumentDialog] =
    React.useState(false);
  const [selectedDoc, setSelectedDoc] = React.useState<null | {
    title: string;
    content: string;
  }>(null);
  const [selectedDocIndex, setSelectedDocIndex] = React.useState<number | null>(
    null,
  );
  const [confirmationOpen, setConfirmationOpen] = React.useState(false);
  const [settingsConfirmationOpen, setSettingsConfirmationOpen] =
    React.useState(false);
  const [selectedSettingsIndex, setSelectedSettingsIndex] = React.useState<
    number | null
  >(null);
  const [openNewSettingsDialog, setOpenNewSettingsDialog] =
    React.useState(false);
  const [newSettingsTitle, setNewSettingsTitle] = React.useState("");
  const [newSettingsDescription, setNewSettingsDescription] =
    React.useState("");
  const [isEditingSettings, setIsEditingSettings] = React.useState(false);
  const [editingSettingsIndex, setEditingSettingsIndex] = React.useState<
    number | null
  >(null);
  const [dropdownBlocks, setDropdownBlocks] = React.useState<any[]>([]);
  const [alldropdownTrees, setAlldropdownTrees] = React.useState<{
    dropdownTrees: any[][];
    configDropdownTrees: any[][];
  }>({ dropdownTrees: [], configDropdownTrees: [] });
  const [submissionAction, setSubmissionAction] = React.useState({
    submitBundleId: "",
    submitRequest: "",
  });
  const [dropdownInput, setDropdownInput] = React.useState<{
    label: string;
    value: string;
  }>({ label: "", value: "" });
  const [dropdownSelections, setDropdownSelections] = React.useState<{
    [key: number]: string;
  }>({});
  const [dependentSelections, setDependentSelections] = React.useState<{
    [key: number]: string;
  }>({});
  const [expandedHelpDoc, setExpandedHelpDoc] = React.useState<boolean>(false);

  // Handle browser back button - navigate to ai-engine
  React.useEffect(() => {
    const handlePopState = () => {
      sessionStorage.removeItem("activeMeshFlow");
      navigate("/ai-engine", { replace: true });
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  // Cleanup sessionStorage on page unload
  React.useEffect(() => {
    const handleBeforeUnload = () => {
      // Keep meshPluginId and editPluginId on reload to preserve plugin creation state
      // Only clear active mesh flow data
      sessionStorage.removeItem("activeMeshFlow");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  React.useEffect(() => {
    const meshPluginId = sessionStorage.getItem("meshPluginId");
    const editPluginId = sessionStorage.getItem("editPluginId");
    const hasSessionPluginId = !!(meshPluginId || editPluginId);
    const resolvedPluginId = meshPluginId || editPluginId || contextPluginId;

    if (!hasSessionPluginId || !resolvedPluginId) {
      sessionStorage.removeItem("activeMeshFlow");
      navigate("/ai-engine", { replace: true });
      return;
    }

    if (!pluginId) {
      setPluginId(resolvedPluginId);
    }
  }, [contextPluginId, navigate, pluginId]);

  // Handle browser back button - navigate to ai-engine
  React.useEffect(() => {
    const handlePopState = () => {
      // Keep meshPluginId and editPluginId to preserve plugin creation context
      // Only clear active mesh flow data
      sessionStorage.removeItem("activeMeshFlow");
      navigate("/ai-engine", { replace: true });
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  // Cleanup sessionStorage on page unload
  React.useEffect(() => {
    const handleBeforeUnload = () => {
      // Keep meshPluginId and editPluginId on reload to preserve plugin creation state
      // Only clear active mesh flow data
      sessionStorage.removeItem("activeMeshFlow");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  React.useEffect(() => {
    const meshPluginId = sessionStorage.getItem("meshPluginId");
    const editPluginId = sessionStorage.getItem("editPluginId");
    const hasSessionPluginId = !!(meshPluginId || editPluginId);
    const resolvedPluginId = meshPluginId || editPluginId || contextPluginId;

    if (!hasSessionPluginId || !resolvedPluginId) {
      sessionStorage.removeItem("activeMeshFlow");
      navigate("/ai-engine", { replace: true });
      return;
    }

    if (!pluginId) {
      setPluginId(resolvedPluginId);
    }
  }, [contextPluginId, navigate, pluginId]);

  // ── Dirty tracking ──────────────────────────────────────────────────────────
  const originalUIPages = React.useRef<string | null>(null);
  const originalHelpDocs = React.useRef<string | null>(null);
  const originalFields = React.useRef<string | null>(null);
  const [isDirty, setIsDirty] = React.useState(false);

  // Two separate refs for two different post-save navigation targets
  const pendingNavigationRef = React.useRef(false); // triggered from dialog
  const continueAfterSaveRef = React.useRef(false); // triggered from continue button

  // Set originals once data is fetched
  React.useEffect(() => {
    if (originalUIPages.current === null && uiPages && uiPages.length > 0) {
      originalUIPages.current = JSON.stringify(uiPages);
    }
  }, [uiPages]);

  React.useEffect(() => {
    if (originalHelpDocs.current === null && helpDocs && helpDocs.length > 0) {
      originalHelpDocs.current = JSON.stringify(helpDocs);
    }
  }, [helpDocs]);

  // Recompute dirty whenever uiPages, helpDocs or fields change
  React.useEffect(() => {
    if (
      originalUIPages.current === null &&
      originalHelpDocs.current === null &&
      originalFields.current === null
    )
      return;

    const uiChanged =
      originalUIPages.current !== null &&
      JSON.stringify(uiPages) !== originalUIPages.current;
    const helpChanged =
      originalHelpDocs.current !== null &&
      JSON.stringify(helpDocs) !== originalHelpDocs.current;
    const fieldsChanged =
      originalFields.current !== null &&
      JSON.stringify(fields) !== originalFields.current;

    setIsDirty(uiChanged || helpChanged || fieldsChanged);
  }, [uiPages, helpDocs, fields]);
  // ────────────────────────────────────────────────────────────────────────────

  React.useEffect(() => {
    if (pluginId) {
      dispatch({ type: MeshPluginActions.SET_PLUGIN_ID, payload: pluginId });
    }
  }, [pluginId, dispatch]);

  // Cleanup sessionStorage on page unload
  React.useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem("activeMeshFlow");
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const { data: fetchedUIPageData, isLoading: isFetchingUIPage } = useQuery(
    ["ui-page-data", pluginId],
    () => fetchUIPageData(pluginId!),
    {
      enabled: !!pluginId && (isEdit || hasUIPage !== false),

      onError: (err: any) => {
        console.error("Failed to load UI pages:", err);
      },
    },
  );

  const { data: configPageData } = useQuery(
    ["configPage", pluginId, "temp"],
    () => fetchConfigPage(pluginId ?? "", "temp"),
    { enabled: !!pluginId && isEdit && hasTempConfigPage },
  );

  const { data: workflowsData } = useQuery(
    ["meshWorkflows", pluginId],
    () => getAllWorkflows(pluginId),
    {
      enabled: !!pluginId && isEdit,
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
  );

  // Redirect to /ai-engine if not in edit mode
  React.useEffect(() => {
    if (!pluginId) return;
    const params = new URLSearchParams(location.search);
    if (params.get("edit") !== "1") {
      navigate("/ai-engine", { replace: true });
    }
  }, [pluginId, location.search, navigate]);

  const settingsWorkflows = React.useMemo(() => {
    const workflows =
      workflowsData?.workflows ||
      workflowsData?.data?.workflows ||
      workflowsData?.data ||
      [];
    if (!Array.isArray(workflows)) return [];
    return workflows.filter((w: any) => w.type_workflow === "settings");
  }, [workflowsData]);

  const actionWorkflows = React.useMemo(() => {
    const workflows =
      workflowsData?.workflows ||
      workflowsData?.data?.workflows ||
      workflowsData?.data ||
      [];
    if (!Array.isArray(workflows)) return [];
    return workflows.filter((w: any) => w.type_workflow === "action");
  }, [workflowsData]);

  const filterConfigKeys = (config: any) => {
    const excludeKeys = [
      "isDecript",
      "isDecrypted",
      "is_decript",
      "testConnectionBtn",
      "tabId",
      "configType",
    ];
    return Object.fromEntries(
      Object.entries(config).filter(([key]) => !excludeKeys.includes(key)),
    );
  };

  React.useEffect(() => {
    if (!configPageData?.data) return;
    const newSavedSettings: Record<string, any[]> = {};
    const newConfigData: any[] = [];
    Object.entries(configPageData.data).forEach(([pageUid, configs]) => {
      const filteredConfigs = (configs as any[]).map(filterConfigKeys);
      newSavedSettings[pageUid] = filteredConfigs;
      newConfigData.push({ pageUid, data: filteredConfigs });
    });
    dispatch({
      type: MeshPluginActions.SET_SAVED_SETTINGS,
      payload: newSavedSettings,
    });
    dispatch({
      type: MeshPluginActions.SET_CONFIGURATION_DATA,
      payload: newConfigData,
    });
  }, [configPageData, dispatch]);

  React.useEffect(() => {
    if (!fetchedUIPageData?.data) return;

    const pages = fetchedUIPageData.data;
    dispatch({ type: MeshPluginActions.SET_UI_PAGES, payload: pages });

    const hasAnyUI = Array.isArray(pages) && pages.length > 0;

    dispatch({
      type: MeshPluginActions.SET_HAS_UI_PAGE,
      payload: hasAnyUI,
    });

    const extractedFields: any[] = [];
    pages
      .filter((page: any) => page.pageType === "action")
      .forEach((page: any) => {
        (page.forms || []).forEach((form: any) => {
          (form.fieldList || []).forEach((field: any) => {
            const cleanNullValues = (obj: any): any => {
              if (obj === null || obj === undefined)
                return Array.isArray(obj) ? [] : obj;
              if (Array.isArray(obj)) return obj.map(cleanNullValues);
              if (typeof obj === "object") {
                const cleaned: any = {};
                for (const [key, value] of Object.entries(obj)) {
                  if (key === "children" || key === "targetFields") {
                    cleaned[key] = value === null ? [] : cleanNullValues(value);
                  } else {
                    cleaned[key] = cleanNullValues(value);
                  }
                }
                return cleaned;
              }
              return obj;
            };

            const normalizedField: any = {
              fieldName: field.fieldTitle || "",
              fieldType: field.fieldType || "",
              defaultValue: field.default || "",
              placeholder: field.placeholder || "",
              infoIcon: field.infoIcon || "",
              dropdownOptions: (field.values || []).map((val: any) => ({
                label: val.displayName || "",
                value: val.value || "",
              })),
              dynamicRequest: field.dynamicRequest || "",
              isApiValue:
                field.values?.some((val: any) => val.isDynamic === 1) ||
                (field.dropdownTree &&
                  Array.isArray(field.dropdownTree) &&
                  field.dropdownTree.length > 0) ||
                false,
              fromAnomaly: !!field.isAnomaly,
              isConfig: !!field.isConfig,
              onloadAction: field.onloadAction || null,
              bundleId: field.bundleId || null,
              requestId: field.requestId || null,
              userAction: field.userAction
                ? {
                    trigger: field.userAction.trigger || "",
                    bundle: field.userAction.bundle || "",
                    bundleVariable: field.userAction.bundleVariable || "",
                    mapResponse: {
                      label: field.userAction.mapResponse?.label || "",
                      value: field.userAction.mapResponse?.value || "",
                    },
                    targetField: field.userAction.targetField || "",
                  }
                : undefined,
            };

            if (
              field.dropdownTree &&
              Array.isArray(field.dropdownTree) &&
              field.dropdownTree.length > 0
            ) {
              normalizedField.dropdownTree = cleanNullValues(
                field.dropdownTree,
              );
              normalizedField.isApiValue = true;
            }
            if (
              field.configDropdownTree &&
              Array.isArray(field.configDropdownTree) &&
              field.configDropdownTree.length > 0
            ) {
              normalizedField.configDropdownTree = cleanNullValues(
                field.configDropdownTree,
              );
              normalizedField.isConfig = true;
            }
            if (field.fieldType === "submit") {
              normalizedField.submitConfig = {
                Ismultiple: field.submitConfig?.Ismultiple || false,
                bundleId: field.submitConfig?.bundleId || "",
                requestId: field.submitConfig?.requestId || "",
                submit_types: field.submitConfig?.submit_types || [],
              };
            }

            extractedFields.push(normalizedField);
          });
        });
      });

    dispatch({
      type: MeshPluginActions.SET_ACTION_FIELDS,
      payload: extractedFields,
    });

    // ← set original fields ref after fetch
    originalFields.current = JSON.stringify(extractedFields);

    const helpDocsData = pages
      .filter((p: any) => p.pageType === "Help")
      .map((page: any) => ({
        title: page.pageTitle,
        content: page.helpContent || page.content || "",
      }));
    dispatch({ type: MeshPluginActions.SET_HELP_DOCS, payload: helpDocsData });
  }, [fetchedUIPageData?.data, dispatch]);

  // ── Mutation ─────────────────────────────────────────────────────────────────
  const { mutate: createUIPage, isLoading: isSavingUIPage } = useMutation({
    mutationFn: saveUIPageInfo,
    onSuccess: () => {
      toast({
        variant: "success",
        title: "Success",
        description: "UI Page saved successfully.",
      });
      queryClient.invalidateQueries(["ui-page-data", pluginId]);

      // Reset dirty state
      originalUIPages.current = JSON.stringify(uiPages);
      originalHelpDocs.current = JSON.stringify(helpDocs);
      originalFields.current = JSON.stringify(fields);
      setIsDirty(false);

      const settingsExist =
        uiPages?.some((p) => p.pageType === "settings") || false;
      if (settingsExist) {
        dispatch({ type: MeshPluginActions.SET_HAS_UI_PAGE, payload: true });
      }

      // ← branch: dialog save → proceed blocker, continue button → navigate
      if (pendingNavigationRef.current) {
        pendingNavigationRef.current = false;
        confirmNavigation();
      } else if (continueAfterSaveRef.current) {
        continueAfterSaveRef.current = false;
        navigate(`/create-node-plugin/flow?edit=1`, {
          state: { pluginId, isEdit },
          replace: true,
        });
      }
      // else: normal save button — stay on page
    },
    onError: (error: any) => {
      pendingNavigationRef.current = false;
      continueAfterSaveRef.current = false;
      toast({
        variant: "destructive",
        title: "Error",
        description: error?.message || "Failed to save UI Page",
      });
    },
  });

  // ← declared after mutation so isSavingUIPage is defined
  const { showDialog, confirmNavigation, cancelNavigation } = useUnsavedChanges(
    isDirty && !isSavingUIPage,
  );

  const handleBack = () => {
    navigate("/create-node-plugin/info?edit=1", {
      state: { pluginId, isEdit: true },
      replace: true,
    });
  };

  const handleContinue = () => {
    if (settingsPages.length === 0) {
      toast({
        variant: "destructive",
        title: "No Settings Page",
        description:
          "Please create at least one settings page before creating logic flow.",
        duration: 3000,
      });
      return;
    }

    navigate(`/create-node-plugin/flow?edit=1`, {
      state: { pluginId, isEdit },
      replace: true,
    });
  };

  const handleSaveSettings = () => {
    if (!pluginId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Plugin ID is missing",
      });
      return;
    }
    const payload = {
      plugin_id: pluginId,
      pages: prepareUIPages(),
      is_edit: hasUIPage === true,
    };
    createUIPage(payload);
  };

  const prepareUIPages = () => {
    const pages: any[] = [];

    uiPages
      ?.filter((p) => p.pageType === "settings")
      .forEach((page) => {
        const cleanedForms = (page.forms || []).map((form: any) => ({
          ...form,
          uid: form.uid || Date.now() + Math.random(),
          fieldList: (form.fieldList || []).map((field: any) => {
            const cleanUserAction =
              field.userAction && Object.keys(field.userAction).length > 0
                ? field.userAction
                : null;

            if (field.fieldType === "testConnectionBtn") {
              const cleanedField: any = {
                ...field,
                userAction: cleanUserAction,
                isAnomaly: field.isAnomaly ?? false,
                isSettings: field.isSettings ?? false,
                isConfig: field.isConfig ?? false,
              };
              if (field.bundleId && field.bundleId.startsWith("workflow_")) {
                cleanedField.workflow_id = field.bundleId;
                cleanedField.bundleId = field.bundleId;
                cleanedField.requestId = "workflow";
              } else {
                cleanedField.requestId = field.requestId || null;
              }
              return cleanedField;
            }

            return {
              ...field,
              userAction: cleanUserAction,
              isAnomaly: field.isAnomaly ?? false,
              isSettings: field.isSettings ?? false,
              isConfig: field.isConfig ?? false,
            };
          }),
        }));

        pages.push({
          uid: page.uid,
          pageTitle: page.pageTitle,
          pageType: "settings",
          infoIcon: page.infoIcon || "info",
          description: page.description || "",
          forms: cleanedForms,
        });
      });

    if (fields && fields.length > 0) {
      pages.push({
        uid: Date.now(),
        pageTitle: "Actions",
        pageType: "action",
        forms: [
          {
            uid: Date.now() + 1,
            formTitle: "Action Form",
            userAction: { requestName: "", action: "", trigger: "" },
            fieldList: fields.map((field: any, index: number) => ({
              fieldId: index + 1,
              fieldTitle: field.fieldName,
              givenName: field.fieldName.toLowerCase().replace(/\s+/g, "_"),
              fieldType: field.fieldType,
              placeholder: field.placeholder || "",
              default: field.defaultValue || "",
              infoIcon: field.infoIcon || "",
              values:
                field.dropdownOptions?.map((opt: any) => ({
                  displayName: opt.label,
                  value: opt.value,
                  default: "",
                  isDynamic: field.isApiValue ? 1 : 0,
                })) || [],
              onloadAction: field.onloadAction || null,
              dropdownTree: field.dropdownTree || null,
              configDropdownTree: field.configDropdownTree || null,
              submitConfig: field.submitConfig
                ? {
                    Ismultiple: field.submitConfig.Ismultiple || false,
                    bundleId: field.submitConfig.bundleId || "",
                    requestId:
                      field.submitConfig.requestId || "all_bundle_requests",
                    submit_types: field.submitConfig.submit_types || [],
                  }
                : null,
              userAction: field.userAction || {
                trigger: "",
                bundle: "",
                bundleVariable: "",
                mapResponse: { label: "", value: "" },
                targetField: "",
              },
              dynamicRequest: field.dynamicRequest || "",
              isAnomaly: field.fromAnomaly || false,
              isSettings: field.fieldType === "import_settings",
              isConfig: field.isConfig || false,
              bundleId: field.bundleId || null,
              requestId: field.requestId || null,
            })),
          },
        ],
      });
    }

    helpDocs?.forEach((doc) => {
      pages.push({
        uid: Date.now() + Math.random(),
        pageTitle: doc.title,
        pageType: "Help",
        infoIcon: "info",
        description: "Help document",
        helpContent: doc.content,
        content: doc.content,
        forms: [],
      });
    });

    return pages;
  };

  const addNewSettingsPage = () => {
    setIsEditingSettings(false);
    setEditingSettingsIndex(null);
    setNewSettingsTitle("");
    setNewSettingsDescription("");
    setOpenNewSettingsDialog(true);
  };

  const handleEditSettingsClick = (index: number, page: UIPageType) => {
    setIsEditingSettings(true);
    setEditingSettingsIndex(index);
    setNewSettingsTitle(page.pageTitle || "");
    setNewSettingsDescription(page.description || "");
    setOpenNewSettingsDialog(true);
  };

  const handleCreateNewSettings = () => {
    if (!newSettingsTitle.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Settings title is required.",
      });
      return;
    }

    if (isEditingSettings && editingSettingsIndex !== null) {
      dispatch({
        type: MeshPluginActions.SET_UI_PAGES,
        payload: uiPages.map((page, idx) =>
          idx === editingSettingsIndex
            ? {
                ...page,
                pageTitle: newSettingsTitle.trim(),
                description: newSettingsDescription.trim(),
              }
            : page,
        ),
      });
      toast({
        variant: "success",
        title: "Settings Updated",
        description: "Settings page updated successfully.",
      });
    } else {
      dispatch({
        type: MeshPluginActions.SET_UI_PAGES,
        payload: [
          ...uiPages,
          {
            uid: Date.now(),
            pageTitle: newSettingsTitle.trim(),
            pageType: "settings",
            infoIcon: "info",
            description: newSettingsDescription.trim(),
            forms: [],
          },
        ],
      });
      toast({
        variant: "success",
        title: "Settings Created",
        description: "New settings page created successfully.",
      });
    }

    setNewSettingsTitle("");
    setNewSettingsDescription("");
    setOpenNewSettingsDialog(false);
    setIsEditingSettings(false);
    setEditingSettingsIndex(null);
  };

  const deleteSettingsPage = (index: number) => {
    setSelectedSettingsIndex(index);
    setSettingsConfirmationOpen(true);
  };

  const onDeleteSettings = () => {
    if (selectedSettingsIndex !== null && uiPages) {
      const updated = [...uiPages];
      if (
        selectedSettingsIndex >= 0 &&
        selectedSettingsIndex < updated.length
      ) {
        updated.splice(selectedSettingsIndex, 1);
        dispatch({ type: MeshPluginActions.SET_UI_PAGES, payload: updated });
        toast({
          variant: "success",
          title: "Deleted",
          description: "Settings page deleted successfully.",
        });
      }
      setSelectedSettingsIndex(null);
    }
    setSettingsConfirmationOpen(false);
  };

  const updatePageData = (index: number, pageData: UIPageType) => {
    if (!uiPages) return;
    const updated = [...uiPages];
    updated[index] = pageData;
    dispatch({ type: MeshPluginActions.SET_UI_PAGES, payload: updated });
  };

  const handleSaveHelpContent = (
    doc: { title: string; content: string },
    isEditing: boolean,
  ) => {
    const trimmedTitle = doc.title.trim();
    const trimmedContent = doc.content.trim();

    if (!trimmedTitle || !trimmedContent || trimmedContent === "<p><br></p>") {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Title and content cannot be empty.",
      });
      return;
    }

    const isDuplicate = helpDocs?.some(
      (d) =>
        d.title.trim().toLowerCase() === trimmedTitle.toLowerCase() &&
        (!isEditing || d.title.trim() !== selectedDoc?.title.trim()),
    );

    if (isDuplicate) {
      toast({
        variant: "destructive",
        title: "Duplicate Title",
        description: `A help document with the title "${trimmedTitle}" already exists.`,
      });
      return;
    }

    const newDoc = { title: trimmedTitle, content: trimmedContent };

    if (isEditing && selectedDoc) {
      dispatch({
        type: MeshPluginActions.SET_HELP_DOCS,
        payload: (prev: { title: string; content: string }[]) =>
          prev.map((d: { title: string; content: string }) =>
            d.title === selectedDoc.title ? newDoc : d,
          ),
      });
      toast({
        variant: "success",
        title: "Updated",
        description: "Help document updated successfully.",
      });
    } else {
      dispatch({
        type: MeshPluginActions.SET_HELP_DOCS,
        payload: [...(helpDocs || []), newDoc],
      });
      toast({
        variant: "success",
        title: "Created",
        description: "Help document created successfully.",
      });
    }

    setOpenAddHelpDocumentDialog(false);
    setSelectedDoc(null);
    setSelectedDocIndex(null);
  };

  const handleDeleteHelpDoc = (index: number) => {
    setSelectedDocIndex(index);
    setConfirmationOpen(true);
  };

  const onDeleteHelpDoc = () => {
    if (selectedDocIndex !== null && helpDocs) {
      const updated = [...helpDocs];
      updated.splice(selectedDocIndex, 1);
      dispatch({ type: MeshPluginActions.SET_HELP_DOCS, payload: updated });
      toast({
        variant: "success",
        title: "Deleted",
        description: "Help document deleted successfully.",
      });
      setSelectedDocIndex(null);
    }
    setConfirmationOpen(false);
  };

  const getFirstLineFromHtml = (htmlContent: string): string => {
    if (!htmlContent) return "No description";
    const content = htmlContent.trim();
    if (/<img\b[^>]*>/i.test(content)) return "🖼️ Image ";
    if (/<iframe\b[^>]*>/i.test(content)) return "🎥 Video ";
    if (/<(ul|ol)\b[^>]*>/i.test(content)) return "📋 List ";
    if (/<table\b[^>]*>/i.test(content)) return "📊 Table ";
    if (/<a\b[^>]*href=/i.test(content)) return "🔗 Link ";
    const textContent = content
      .replace(/<[^>]+>/g, "")
      .replace(/\s+/g, " ")
      .trim();
    if (!textContent) return "No description";
    const firstLine = textContent.split("\n")[0].trim();
    const firstSentence = textContent.split(". ")[0].trim();
    const result = firstLine || firstSentence;
    return result.length > 100 ? result.substring(0, 100) + "..." : result;
  };

  const settingsPages = uiPages?.filter((p) => p.pageType === "settings") || [];
  const helpDocuments =
    uiPages
      ?.filter((p) => p.pageType === "Help")
      .map((page) => ({
        title: page.pageTitle,
        content: page.helpContent || page.content || "",
      })) || [];

  // open Preview page when create-config=1
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const createConfig = urlParams.get("create-config");
    if (createConfig === "1") {
      setOpenPreview(true);
      setActiveTab("settings");
      urlParams.delete("create-config");
      window.history.replaceState({}, "", `?${urlParams.toString()}`);
    }
  }, []);

  const helpDocRef = React.useRef<HTMLDivElement>(null);

  return (
    <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
      <div className="mx-auto max-w-6xl space-y-4">
        <div className="space-y-2 p-2 flex items-center justify-center gap-2 text-muted-foreground">
          <Settings2 size={22} />
          <p className=" text-2xl !mt-0 pb-0">UI Pages.</p>
        </div>
        <PageLoader show={isFetchingUIPage} />
        <div className="!mt-2 bg-gray-50 to-background rounded-2xl border border-secondary bg-background shadow-md backdrop-blur-sm p-3 transition-all duration-300 w-[900px] h-[72vh]">
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full h-full flex flex-col bg-gray-50"
          >
            <div className="flex items-center justify-between border-secondary px-2 mt-4">
              <TabsList className="grid w-fit grid-cols-2 bg-background rounded-xl border border-secondary h-[38px] px-2">
                <TabsTrigger
                  value="settings"
                  className="w-[150px] rounded-lg h-8 data-[state=active]:bg-primary data-[state=active]:text-background"
                >
                  <Settings size={15} className="mr-2" /> Settings
                </TabsTrigger>
                <TabsTrigger
                  value="actions"
                  className="w-[150px] rounded-lg h-8 data-[state=active]:bg-primary data-[state=active]:text-background"
                >
                  <Zap size={15} className="mr-2" /> Actions
                </TabsTrigger>
              </TabsList>
              <div className="flex justify-between items-center">
                {activeTab === "settings" && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      const hasValidSettings = settingsPages.some(
                        (p) => p.forms && p.forms.length > 0,
                      );
                      const hasHelpContent = helpDocs && helpDocs.length > 0;
                      if (!hasValidSettings && !hasHelpContent) {
                        toast({
                          variant: "destructive",
                          title: "Nothing to preview",
                          description:
                            "Add at least one form or help document to preview.",
                        });
                        return;
                      }
                      setOpenPreview(true);
                    }}
                    className="gap-2  rounded-xl"
                  >
                    <Eye size={18} />
                    Preview
                  </Button>
                )}
              </div>
            </div>

            <TabsContent
              value="settings"
              className="mt-6 px-2 flex-1 overflow-auto"
            >
              <div className="space-y-4">
                <div className="border border-secondary rounded-2xl p-4 shadow-[0_2px_4px_rgba(0,0,0,0.09)] bg-background">
                  <div
                    className="flex w-full justify-between items-center cursor-pointer transition-all duration-300 "
                    onClick={() => {
                      setExpandedHelpDoc((prev) => !prev);
                    }}
                  >
                    <div
                      className="flex items-center gap-3 mb-1"
                      title={`Click to ${expandedHelpDoc ? "Collapse" : "Expand"} Help Documents`}
                    >
                      <div>
                        <FileText size={18} className="text-muted-foreground" />
                      </div>
                      <div className=" flex flex-col">
                        <p className="text-sm font-semibold flex items-center">
                          Help Documents{" "}
                          {helpDocs?.length > 0 && (
                            <span className="font-normal text-sm p-1 rounded-full bg-primary text-background h-4 w-4 flex items-center justify-center ml-1">
                              {helpDocs.length}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Create help documents to guide users for plugin usage.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3 items-center">
                      <div
                        className="p-1 bg-muted rounded-md flex items-center justify-center"
                        title={`Click to ${expandedHelpDoc ? "Collapse" : "Expand"} Help Documents`}
                      >
                        <ChevronDown
                          size={18}
                          className={`transition-transform duration-300 text-muted-foreground ${
                            expandedHelpDoc ? "rotate-180" : ""
                          }`}
                        />
                      </div>
                      <div
                        className="text-primaryBlue flex items-center gap-1 text-sm w-fit cursor-pointer hover:text-muted-foreground transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDoc(null);
                          setOpenAddHelpDocumentDialog(true);
                        }}
                      >
                        <CirclePlus size={15} /> Add Help Document
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      maxHeight: expandedHelpDoc
                        ? `${helpDocRef.current?.scrollHeight ? helpDocRef.current.scrollHeight + 10 : 0}px`
                        : "0px",
                    }}
                    className="overflow-hidden transition-all duration-500 ease-in-out"
                  >
                    <div
                      className="flex gap-4 flex-wrap border-t border-secondary mt-2 pb-1"
                      ref={helpDocRef}
                    >
                      {helpDocs && helpDocs.length > 0 ? (
                        helpDocs.map((doc, idx) => (
                          <div
                            key={idx}
                            className=" mt-5 min-w-[220px] group border border-lightBlue flex items-center justify-between gap-2 py-4 px-4 shadow-[0_2px_4px_rgba(0,0,0,0.09)] transition-all duration-300 hover:-translate-y-1.1 hover:shadow-[0_2px_4px_rgba(0,0,0,0.2)] rounded-xl w-full sm:w-auto transition-colors cursor-pointer border-l-[5px] border-l-primaryBlue hover:text-foreground"
                            onClick={() => {
                              setSelectedDoc(doc);
                              setOpenAddHelpDocumentDialog(true);
                            }}
                          >
                            <div className="flex items-center gap-1">
                              <span className="text-[22px]">📄</span>
                              <div className="flex-1 overflow-hidden">
                                <p className="text-foreground text-sm font-medium font-mono">
                                  {doc.title}
                                </p>
                                <p className="text-[10px] text-muted-foreground truncate max-w-[130px]">
                                  {getFirstLineFromHtml(doc.content)}
                                </p>
                              </div>
                            </div>
                            <span
                              className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteHelpDoc(idx);
                              }}
                            >
                              <Trash size={14} />
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="w-full flex justify-center items-center">
                          <FallBackUi
                            title="No Help documents added yet !"
                            description="Click 'Add Help Document' to create a help document."
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="border border-secondary rounded-2xl p-4 relative shadow-[0_2px_4px_rgba(0,0,0,0.09)] bg-background !mb-14">
                  <div className="flex justify-between items-center border-b border-secondary pb-2 mb-4">
                    <div className="flex items-center gap-4 mb-1">
                      <div>
                        <SlidersHorizontal
                          size={17}
                          className="text-muted-foreground"
                        />
                      </div>
                      <div className=" flex flex-col">
                        <p className="text-sm font-semibold">Settings Page</p>
                        <p className="text-xs text-muted-foreground">
                          Create settings page to configure the plugin.
                        </p>
                      </div>
                    </div>
                    {settingsPages.length === 0 && (
                      <div
                        className="flex items-center gap-1 text-sm text-primaryBlue cursor-pointer hover:text-muted-foreground transition-colors"
                        onClick={addNewSettingsPage}
                      >
                        <CirclePlus size={15} /> Add New Settings
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 pb-5 px-1">
                    {settingsPages.length > 0 ? (
                      settingsPages.map((page, index) => (
                        <div
                          key={page.uid}
                          className="group relative w-full rounded-xl shadow-[0_2px_4px_rgba(0,0,0,0.09)] border border-lightBlue bg-background p-4 pb-5 transition-all duration-300 hover:-translate-y-1.1 hover:shadow-[0_2px_4px_rgba(0,0,0,0.2)]"
                        >
                          <div className="flex w-full justify-between items-center border-b">
                            {page.pageTitle && (
                              <p className="text-md text-primaryBlue font-semibold capitalize px-1">
                                {page.pageTitle}
                              </p>
                            )}
                            <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity pb-1">
                              <div
                                className="flex items-center justify-center p-1 "
                                title="Edit Settings"
                              >
                                <SquarePen
                                  onClick={() =>
                                    handleEditSettingsClick(index, page)
                                  }
                                  size={14}
                                  className="text-muted-foreground cursor-pointer hover:text-foreground"
                                />
                              </div>
                              <div
                                className="flex items-center justify-center p-1"
                                title="Delete Settings"
                              >
                                <Trash
                                  onClick={() => deleteSettingsPage(index)}
                                  className="text-muted-foreground cursor-pointer hover:text-primary"
                                  size={14}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-between items-center mb-3">
                            <div className="flex-1 mt-1">
                              {page.description && (
                                <p className="text-xs text-muted-foreground mt-1 max-w-[90%] max-h-[30px] overflow-hidden text-ellipsis px-1">
                                  {page.description}
                                </p>
                              )}
                            </div>
                          </div>
                          <SettingsUIPage
                            pageData={page}
                            onSave={(data) => updatePageData(index, data)}
                            pluginId={pluginId}
                            meshVersion="2.0"
                            settingsWorkflows={settingsWorkflows}
                          />
                        </div>
                      ))
                    ) : (
                      <div className="w-full flex justify-center items-center">
                        <FallBackUi
                          title="No Settings pages added yet !"
                          description="Click 'Add New Settings' to create a settings page."
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex justify-end absolute bottom-4 right-7 shadow-[0_5px_5px_rgba(0,0,0,0.09)]">
                <Button
                  onClick={handleSaveSettings}
                  disabled={
                    isSavingUIPage ||
                    (settingsPages.length === 0 && helpDocuments.length === 0)
                  }
                  className="bg-transparent text-primary border border-primary hover:bg-primary hover:text-background transition-colors"
                >
                  {isSavingUIPage ? (
                    <Loader size={18} className="animate-spin mr-1" />
                  ) : (
                    <Save size={16} className="mr-2" />
                  )}
                  {isSavingUIPage ? <>Saving Settings...</> : "Save Settings"}
                </Button>
              </div>
            </TabsContent>

            <TabsContent
              value="actions"
              className="mt-6 flex-1 overflow-auto px-2 relative"
            >
              <div>
                <ActionsPage
                  dropdownInput={dropdownInput}
                  setDropdownInput={setDropdownInput}
                  dropdownSelections={dropdownSelections}
                  setDropdownSelections={setDropdownSelections}
                  dependentSelections={dependentSelections}
                  setDependentSelections={setDependentSelections}
                  dropdownBlocks={dropdownBlocks}
                  setDropdownBlocks={setDropdownBlocks}
                  alldropdownTrees={alldropdownTrees}
                  setAlldropdownTrees={setAlldropdownTrees}
                  submissionAction={submissionAction}
                  setSubmissionAction={setSubmissionAction}
                  meshVersion="2.0"
                  settingsWorkflows={settingsWorkflows}
                  actionWorkflows={actionWorkflows}
                  className="bg-background"
                />
              </div>
              <div className="sticky bottom-4 right-4 flex justify-end z-10 mt-8">
                <Button
                  onClick={handleSaveSettings}
                  disabled={isSavingUIPage || !fields || fields.length === 0}
                  className="bg-transparent border border-primary text-primary hover:bg-primary hover:text-background transition-colors"
                >
                  {isSavingUIPage ? (
                    <>
                      <Loader size={18} className="animate-spin mr-1" /> Saving
                      Actions...
                    </>
                  ) : (
                    "Save Actions"
                  )}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* ← Unsaved changes dialog */}
        <UnsavedChangesDialog
          open={showDialog}
          isSaving={isSavingUIPage}
          onCancel={cancelNavigation}
          onDiscard={() => {
            sessionStorage.removeItem("activeMeshFlow");
            confirmNavigation();
          }}
          onSave={() => {
            pendingNavigationRef.current = true;
            handleSaveSettings();
          }}
        />

        <div className="sticky bottom-6 flex justify-end gap-3">
          <div className="flex justify-between items-center w-full">
            <Button variant="outline" onClick={handleBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to General Info
            </Button>
            <Button
              onClick={handleContinue}
              disabled={
                isSavingUIPage ||
                settingsPages.length === 0 ||
                settingsPages.some((p) => !p.forms || p.forms.length === 0)
              }
            >
              {isSavingUIPage && continueAfterSaveRef.current
                ? "Saving..."
                : "Continue to Logic Flow"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        <HelpDocumentDialog
          open={openAddHelpDocumentDialog}
          handleClose={() => {
            setOpenAddHelpDocumentDialog(false);
            setSelectedDoc(null);
          }}
          handleSave={handleSaveHelpContent}
          initialDoc={selectedDoc}
        />

        <Dialog
          open={openNewSettingsDialog}
          onOpenChange={setOpenNewSettingsDialog}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {isEditingSettings ? "Edit Settings Page" : "New Settings Page"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label htmlFor="settings-title">Title</Label>
                <Input
                  id="settings-title"
                  value={newSettingsTitle}
                  onChange={(e) => setNewSettingsTitle(e.target.value)}
                  placeholder="Enter settings title"
                />
              </div>
              <div>
                <Label htmlFor="settings-description">Description</Label>
                <Textarea
                  id="settings-description"
                  value={newSettingsDescription}
                  onChange={(e) => setNewSettingsDescription(e.target.value)}
                  placeholder="Enter settings description"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setOpenNewSettingsDialog(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleCreateNewSettings}>
                  {isEditingSettings ? "Update" : "Create"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Confirmation
          text="Are you sure you want to delete this help document?"
          show={confirmationOpen}
          confirmAction={onDeleteHelpDoc}
          confirmText="Delete"
          rejectText="Cancel"
          rejectAction={() => setConfirmationOpen(false)}
          closeDialog={() => setConfirmationOpen(false)}
        />

        <Confirmation
          text="Are you sure you want to delete this settings page? All forms in this page will be deleted."
          show={settingsConfirmationOpen}
          confirmAction={onDeleteSettings}
          confirmText="Delete"
          rejectText="Cancel"
          rejectAction={() => setSettingsConfirmationOpen(false)}
          closeDialog={() => setSettingsConfirmationOpen(false)}
        />

        {openPreview && (
          <UiPreview
            pluginId={pluginId || ""}
            open={openPreview}
            setOpenPreview={setOpenPreview}
            pages={uiPages ?? []}
          />
        )}
      </div>
    </div>
  );
};

const CreatePluginUIPage: React.FC = () => {
  return (
    <MeshPluginProvider>
      <CreatePluginUIPageContent />
    </MeshPluginProvider>
  );
};

export default CreatePluginUIPage;
